import s from './Map.module.sass'
import { MapRestCarouselMobile } from './ui/MapRestCarouselMobile'

import React, { FC, useContext, useEffect, useState } from 'react'
import { load } from '@2gis/mapgl'
import { useRouter } from 'next/router'

import { globalStore, useAppDispatch, useAppSelector } from '@app/model/store'

import { closeMapAC, updateMapData } from '@widgets/Restaurant/Map/model/actions'
import { Header } from '@widgets/Common/Header'
import { MapButtonClose } from '@widgets/Restaurant/Map/ui/MapButtonClose'
import { MapContext } from '@widgets/Restaurant/Map/MapContext'
import { MapRestCard } from '@widgets/Restaurant/Map/ui/MapRestCard'
import { MapRestGrid } from '@widgets/Restaurant/Map/ui/MapRestGrid'
import { MapScaleControls } from '@widgets/Restaurant/Map/ui/MapScaleControls'
import { useBlockScrollOverlay } from '@shared/lib/hooks/useBlockScrollOverlay'
import { useHandleMarkers } from '@widgets/Restaurant/Map/hooks/useHandleMarkers'
import { useMapTitle } from '@widgets/Restaurant/Map/hooks/useMapTitle'
import { MapFiltersAside } from '@widgets/Restaurant/Map/ui/MapFiltersAside/MapFiltersAside'

import { useClientResponsive } from '@shared/lib/hooks/useClientResponsive'
import { Title } from '@shared/ui/Typography/Title'
import LoadingSpinner from '../../../6_shared/ui/Feedback/LoadingSpinner/LoadingSpinner'
import { MapSelectors } from '.'
import { EGG } from '@shared/api/analytics'
import { MapUserLocation } from '@widgets/Restaurant/Map/ui/MapUserLocation'
import { setMapGeolocation } from '@widgets/Restaurant/Map/model/thunks'
import { Checkbox } from '@shared/ui/Form/Single/Checkbox'
import { useFilters } from '@features/Restaurant/RestaurantFilters/helpers/helper'
import RestaurantFilters from '@features/Restaurant/RestaurantFilters/RestaurantFilters'

const IS_PROD = process.env.GITLAB_STATUS === '0'

const MapContent: FC = () => {
  const cityCoordinates = useAppSelector((store) => store.global.cityRead.coordinates)
  const initSlugsFilters = useAppSelector((store) => store.features.restaurant_filters.output.slugs)
  const { isMobile } = useClientResponsive()
  const { active, map: mapStore } = useAppSelector(MapSelectors.all)
  const { grid: isShowGrid, card: isShowCard } = useAppSelector(MapSelectors.viewsDesktop)
  const { status: restsStatus } = useAppSelector(MapSelectors.restaurants)
  const [mapInstance, setMapInstance] = useContext(MapContext)
  const [updateMapAfterMove, setUpdateMapAfterMove] = useState(true)
  const [mapLoaded, setMapLoaded] = useState(false)
  const dispatch = useAppDispatch()
  const router = useRouter()
  const [asideFilters, setAsideFilters] = useState<boolean>(false)
  const [_] = useFilters(initSlugsFilters, false)

  useHandleMarkers(mapInstance)

  useBlockScrollOverlay()

  const handleClose = () => {
    EGG.pages.Map.click_close()
    dispatch(closeMapAC())
  }

  useEffect(() => {
    if (isMobile || !mapInstance.mapCreated) return

    if (isShowGrid && isShowCard) mapInstance.mapCreated.setPadding({ left: 880 })
    else if (isShowGrid || isShowCard) mapInstance.mapCreated.setPadding({ left: 440 })
    else mapInstance.mapCreated.setPadding({ left: 0 })
  }, [!!mapInstance.mapCreated, isShowGrid, isShowCard])

  // Создание карты
  useEffect(() => {
    try {
      if (!active) return
      load().then((mapglAPI) => {
        if (!document.getElementById('global-map')) return
        const mapStoreData = globalStore.getState().features.map.map

        const initCoords = mapStoreData.center
          ? mapStoreData.center
          : cityCoordinates
          ? [cityCoordinates[1], cityCoordinates[0]]
          : [37.6156, 55.7522]
        const initZoom = mapStoreData.zoom || 13
        const map = new mapglAPI.Map('global-map', {
          lang: 'ru',
          controlsLayoutPadding: { top: 20, bottom: 20, left: 16, right: 16 },
          center: initCoords,
          zoom: initZoom,
          maxZoom: 19,
          minZoom: 7,
          zoomControl: false,
          key: '9c84424f-237a-4c66-bc7e-26bc4f49e606',
          style: '4eda4784-9adb-4c0f-8380-367cad53599c',
          enableTrackResize: true,
          copyright: 'bottomRight',
          trafficControl: false,
        })

        setMapInstance(() => ({ mapglAPI, mapCreated: map }))

        dispatch(setMapGeolocation({ mapInstance: { mapglAPI, mapCreated: map }, eventType: 'init' }))

        setMapLoaded(true)
      })
    } catch (error) {
      setMapLoaded(true)
      console.error('Ошибка инициализации карты', error)
    }
    return () => {
      mapInstance?.mapCreated?.destroy()
      setMapInstance(() => ({ mapglAPI: null, mapCreated: null }))
    }
  }, [active])

  useEffect(() => {
    if (!mapInstance?.mapCreated) return

    const isDiffCenter = mapInstance?.mapCreated.getCenter().some((coord, i) => coord !== mapStore.center[i])
    const isDiffZoom = mapStore.zoom !== mapInstance?.mapCreated.getZoom()

    if (isDiffCenter || isDiffZoom) {
      mapInstance?.mapCreated.setCenter(mapStore.center)
      mapInstance?.mapCreated.setZoom(mapStore.zoom)
    }
  }, [mapStore.zoom, mapStore.center])

  useEffect(() => {
    if (!mapInstance?.mapCreated) return

    function sendUpdateMapData({ isUser }) {
      if (!globalStore.getState().features.map.active) {
        this.off('idle', sendUpdateMapData)
        return
      }
      const { northEast, southWest } = mapInstance.mapCreated.getBounds()

      dispatch(
        updateMapData(
          {
            // @ts-ignore
            bbox: { northEast, southWest },
            // @ts-ignore
            center: mapInstance.mapCreated.getCenter(),
            zoom: mapInstance.mapCreated.getZoom(),
          },
          isUser,
        ),
      )
    }

    if (updateMapAfterMove) mapInstance.mapCreated.on('idle', sendUpdateMapData)
    return () => {
      mapInstance.mapCreated.off('idle', sendUpdateMapData)
    }
  }, [updateMapAfterMove, mapInstance.mapCreated])

  const handleChangeCancelUpdate = (option, checked) => {
    setUpdateMapAfterMove(checked)
  }

  const isShowHeader = !isMobile && router.pathname !== '/[city_slug]/restaurants/restorator'

  const mapTitle = useMapTitle()

  return (
    <div className={s.container} data-map-loaded={mapLoaded ? 'true' : 'false'}>
      {isShowHeader && (
        <div className={s['header-wrapper']}>
          <Header />
        </div>
      )}

      {isMobile ? (
        <div className={s['content-mobile']} style={{ height: window.innerHeight + 'px' }}>
          <div id='global-map' className={s.map}></div>
          <div className={s.header}>
            <Title className={s.title} sizes={'h4'}>
              {mapTitle}
            </Title>
            <MapButtonClose onClick={handleClose} className={s['button-close']} />
          </div>

          <div className={s.footer}>
            <div className={s['footer-buttons']}>
              <MapUserLocation className={s['footer-button-location']} />

              <RestaurantFilters
                reloadMap={true}
                hideDivider={true}
                selected={null}
                className={s['mobile-filters']}
                filters={[
                  'hasBooking',
                  'metro',
                  'rating',
                  'price',
                  'kitchen',
                  'establishment',
                  'afishaSelected',
                  'openNow',
                ]}
              />
            </div>
            <MapRestCarouselMobile />
          </div>
        </div>
      ) : (
        <div className={s['content-desktop']}>
          <div id='global-map' className={s.map}></div>

          <div className={s.aside}>
            {isShowGrid && !asideFilters && <MapRestGrid onExtraButtonClick={() => setAsideFilters(true)} />}
            {isShowGrid && asideFilters && <MapFiltersAside handleClose={() => setAsideFilters(false)} />}
            {isShowCard && <MapRestCard />}
          </div>

          <div className={s['cancel-update-button']}>
            <Checkbox
              mode={'checkbox'}
              option={{
                id: 'cancel-update',
                name: 'cancel-update',
                label: 'Обновлять список при перемещении',
              }}
              checked={updateMapAfterMove}
              onChange={handleChangeCancelUpdate}
              className={s['cancel-update-checkbox']}
            />
          </div>

          <div className={s.controls}>
            <MapButtonClose onClick={handleClose} className={s['button-close']} />
            {!IS_PROD && <LoadingSpinner loading={restsStatus === 'loading'} size={20} className={s.loading} />}
            <div className={s['zoom-and-location']}>
              <MapScaleControls />
              <MapUserLocation />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MapContent
